import type { ComponentProps } from 'react';
import type React from 'react';
import { useEffect } from 'react';

import { cn } from '@change/ds-utils';

import { parseHtml } from '@change-corgi/core-react/html';

type Props = {
	html: string;
	aspectRatio?: number;
};

const EmbeddedMediaInner: React.FC<Props & ComponentProps<'div'>> = ({
	html,
	aspectRatio,
	className,
	style,
	...rest
}) => {
	return (
		<div
			className={cn({ '[&>iframe]:aspect-[var(--embed-ar)] [&>iframe]:h-auto': !!aspectRatio }, className)}
			style={aspectRatio ? ({ ...style, '--embed-ar': String(aspectRatio) } as React.CSSProperties) : style}
			suppressHydrationWarning
			{...rest}
		>
			{parseHtml(html, {
				allowedTags: ['iframe'],
				allowedAttrs: [
					'scrolling',
					'frameborder',
					'allowfullscreen',
					'allow',
					// https://iframely.com/docs/lazy-load#what-you-get
					'data-iframely-url',
					'data-img',
				],
				suppressHydrationWarning: true,
			})}
		</div>
	);
};

const SCRIPT_ID = 'iframely-embed-script';

export const EmbeddedMedia: React.FC<Props & ComponentProps<'div'>> = (props) => {
	const { html } = props;
	const accessibleHtml = html.replace(/<iframe\b/gi, '<iframe aria-label="Media"');

	useEffect(() => {
		if (document.getElementById(SCRIPT_ID)) return;
		const script = document.createElement('script');
		script.id = SCRIPT_ID;
		script.async = true;
		script.setAttribute('charset', 'utf-8');
		script.src = 'https://iframely.net/embed.js';
		document.head.appendChild(script);
	}, []);

	return <EmbeddedMediaInner {...props} html={accessibleHtml} />;
};
